import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Credenza,
  CredenzaContent,
  CredenzaDescription,
  CredenzaHeader,
  CredenzaTrigger,
} from "@/components/ui/credenza";
import { Event } from "@/types/prismaTypes";
import { AlertCircle, Loader2, Wallet } from "lucide-react";
import { useParams } from "next/navigation";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useState, useEffect } from "react";
import CartPayment from "./cart-payment";
import { Link1Icon } from "@radix-ui/react-icons";
import Link from "next/link";
import { useLocalStorage } from "usehooks-ts";
import { useTranslations } from "next-intl";
import { Label } from "../ui/label";
import { get } from "../../api/public.client";

interface EventData {
  events: Event[];
}

const hardcodedResetOrgMessages = {
  konnectMessage: "Tunisian TND",
  stripeMessage: "Stripe Euro",
};

export default function CartFooter({
  total,
  validateCart,
  validateCartForm,
  step,
}: {
  total: number | undefined;
  step: string;
  validateCart: any;
  validateCartForm: () => boolean;
}) {
  const t = useTranslations("CartFooter");
  const params = useParams();

  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingReserve, setLoadingReserve] = useState(false);
  const [checked, setChecked] = useState(true);
  const [stripeSupport, setStripeSupport] = useState(false);
  const [showSpecialText, setShowSpecialText] = useState(false);
  const [konnectLoading, setKonnectLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const [localStorageCart] = useLocalStorage<EventData | null>("cart", null);
  const approvalRequired = localStorageCart?.events.find(
    (e: any) => e.id == params?.event_id,
  )?.approvalRequired;

  const cashAccepted = localStorageCart?.events.find(
    (e: any) => e.id == params?.event_id,
  )?.cashAccepted;

  const cardAccepted = localStorageCart?.events.find(
    (e: any) => e.id == params?.event_id,
  )?.cardAccepted;

  useEffect(() => {
    if (!params?.event_id) return;
    const fetchStripeSupport = async () => {
      get("/public/events/{id}/stripe-support", {
        params: {
          path: {
            id: params?.event_id as string,
          },
        },
      }).then((fetchResponse: any) => {
        if (!fetchResponse.error) {
          setStripeSupport(fetchResponse.data.stripeSupport);
        }
      });
    };
    fetchStripeSupport();
  }, [params?.event_id]);

  useEffect(() => {
    if (!params?.event_id) return;
    const fetchEventData = async () => {
      get("/public/events/{id}", {
        params: {
          path: {
            id: params?.event_id as string,
          },
          query: {
            ext: "false",
            ref: "",
          },
        },
      }).then((fetchResponse: any) => {
        if (!fetchResponse.error) {
          const event: Event = fetchResponse.data;
          if ((event.organization?.id as any) == "3bpo7k") {
            setShowSpecialText(true);
          }
        }
      });
    };
    fetchEventData();
  }, [params?.event_id]);

  return (
    <div className="flex flex-col w-full gap-3">
      {step != "Checkout" && (
        <>
          <div className="flex items-center space-x-2">
            <Checkbox id="terms" checked disabled />
            <Label
              htmlFor="terms"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("termsAndConditionsLabel")}
            </Label>
            <Link
              href="https://tunis.events/terms-and-conditions"
              target="_blank"
            >
              <Link1Icon className="-rotate-45" />
            </Link>
          </div>
          <div className="flex items-center mt-2 space-x-2">
            <Checkbox
              id="terms"
              checked={checked}
              onCheckedChange={(e: boolean) => setChecked(e)}
            />
            <Label
              htmlFor="terms"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("hearAboutEventsLabel")}
            </Label>
          </div>
        </>
      )}

      {step == "Checkout" && !cashAccepted && !cardAccepted && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle>{t("title")}</AlertTitle>
          <AlertDescription>{t("errorAlert.description")}</AlertDescription>
        </Alert>
      )}
      {step == "Checkout" &&
        total != undefined &&
        total > 0 &&
        cardAccepted && (
          <>
            {!approvalRequired ? (
              <div className="flex flex-col gap-2 w-full">
                <Button
                  data-umami-event="Payment Konnect"
                  className="justify-start w-full text-left text-md"
                  onClick={() => {
                    if (validateCartForm && validateCartForm()) {
                      setDisabled(true);
                      setKonnectLoading(true);
                      validateCart(false, "KONNECT");
                    } else {
                      setKonnectLoading(false);
                      setDisabled(false);
                    }
                  }}
                  disabled={disabled || konnectLoading || stripeLoading}
                >
                  {konnectLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wallet className="mr-2" />
                  )}
                  {showSpecialText
                    ? `${hardcodedResetOrgMessages.konnectMessage} ${total} TND`
                    : `Carte bancaire ${total} TND`}
                </Button>
                {stripeSupport && (
                  <Button
                    data-umami-event="Payment Stripe"
                    className="justify-start w-full text-left text-md"
                    variant="outline"
                    onClick={() => {
                      if (validateCartForm && validateCartForm()) {
                        setDisabled(true);
                        setStripeLoading(true);
                        validateCart(false, "STRIPE");
                      } else {
                        setStripeLoading(false);
                        setDisabled(false);
                      }
                    }}
                    disabled={disabled || konnectLoading || stripeLoading}
                  >
                    {stripeLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wallet className="mr-2" />
                    )}
                    {showSpecialText
                      ? `${hardcodedResetOrgMessages.stripeMessage} ${total} TND`
                      : `Stripe ${total} TND`}
                  </Button>
                )}
              </div>
            ) : (
              <Credenza>
                <CredenzaTrigger className="w-full" asChild>
                  <Button className="w-full text-lg">
                    {t("proceedAndAwaitApprovalLabel")}
                  </Button>
                </CredenzaTrigger>
                <CredenzaContent>
                  <CredenzaHeader>
                    <CredenzaDescription>
                      <CartPayment validateCart={validateCart} />
                    </CredenzaDescription>
                  </CredenzaHeader>
                </CredenzaContent>
              </Credenza>
            )}
          </>
        )}

      {step == "Checkout" && total != undefined && total == 0 && (
        <>
          {!approvalRequired && (
            <Button
              data-umami-event="Checkout Cart Proceed"
              className="w-full mt-5 text-lg"
              disabled={disabled}
              onClick={() => {
                if (validateCartForm()) {
                  validateCart();
                  setDisabled(true);
                  setLoading(true);
                }
              }}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("proceedLabel")}
            </Button>
          )}
          {approvalRequired && (
            <Credenza>
              <CredenzaTrigger className="w-full">
                <Button
                  className="w-full text-md"
                  data-umami-event="Proceed and await approval"
                >
                  {t("proceedAndAwaitApprovalLabel")}
                </Button>
              </CredenzaTrigger>
              <CredenzaContent>
                <CredenzaHeader>
                  <CredenzaDescription>
                    <CartPayment validateCart={validateCart} />
                  </CredenzaDescription>
                </CredenzaHeader>
              </CredenzaContent>
            </Credenza>
          )}
        </>
      )}

      {step != "Checkout" && (
        <Button
          data-umami-event="Proceed and add to cart"
          data-umami-event-cart-amount={"Cart total: " + total}
          className="w-full mt-5 text-lg"
          disabled={disabled}
          onClick={() => {
            validateCart();
            setDisabled(true);
            setLoading(true);
          }}
        >
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("proceedLabel")}
        </Button>
      )}

      {step == "Checkout" &&
        total != 0 &&
        cashAccepted &&
        !approvalRequired && (
          <Button
            disabled={disabled}
            className="w-full text-md"
            variant="ghost"
            onClick={() => {
              validateCart(true);
              if (validateCartForm && validateCartForm()) {
                setDisabled(true);
                setLoadingReserve(true);
              }
            }}
          >
            {loadingReserve && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {t("reserveAndPayLaterLabel")}
          </Button>
        )}
    </div>
  );
}

export async function getServerSideProps(context: any) {
  return {
    props: {
      messages: (await import(`@/public/i18n/${context.locale}.json`)).default,
    },
  };
}
